/**
 * OfflineIndicator -- Sprint 14, SDD 7.6, FR-OFFLINE-03
 * Visible banner when offline or when queued punches exist.
 */

import { useEffect, useState } from 'react';
import { getPendingCount, onQueueChange, isSyncing } from '../lib/offline';

export default function OfflineIndicator() {
  const [online, setOnline] = useState(typeof navigator !== 'undefined' ? navigator.onLine !== false : true);
  const [pendingCount, setPendingCount] = useState(0);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    const goOnline = () => {
      setOnline(true);
      setSyncing(isSyncing());
    };
    const goOffline = () => {
      setOnline(false);
      setSyncing(false);
    };

    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
    };
  }, []);

  useEffect(() => {
    getPendingCount().then(setPendingCount);
    const unsubscribe = onQueueChange((count) => {
      setPendingCount(count);
      setSyncing(isSyncing());
    });
    return unsubscribe;
  }, []);

  if (online && pendingCount === 0) return null;

  if (online && pendingCount > 0) {
    return (
      <div
        role="status"
        aria-live="polite"
        className="w-full px-4 py-2.5 bg-action-primary/10 border border-action-primary/25 rounded-xl
                   flex items-center gap-2.5 mb-3"
      >
        <span className="text-sm text-action-primary">
          {syncing
            ? `Syncing ${pendingCount} queued punch${pendingCount !== 1 ? 'es' : ''}...`
            : `${pendingCount} punch${pendingCount !== 1 ? 'es' : ''} waiting to sync`}
        </span>
      </div>
    );
  }

  return (
    <div
      role="status"
      aria-live="assertive"
      className="w-full px-4 py-2.5 bg-danger-500/15 border border-danger-500/25 rounded-xl
                 flex items-center justify-between mb-3"
    >
      <span className="text-sm text-danger-400">You are offline</span>
      {pendingCount > 0 && <span className="text-xs text-danger-400/80">{pendingCount} queued</span>}
    </div>
  );
}
